import { ArrowDownward, ArrowUpward, CloseRounded } from '@mui/icons-material';
import { Alert, CircularProgress, IconButton, Paper, Switch, Table, TableBody, TableCell, TableContainer, TableRow } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { useEffect, useState } from 'react';
import { media, media as MediaInterface } from '../../../../../interfaces/interfaces';
import { MediaComponentInterface } from '../../../../../interfaces/pagebuilder';
import { useSelector } from '../../../../../reduxToolkit/typedRedux';
import { getAllMedia, getOrgMedia, updateMedia } from '../../../../../services/services';
import MediaScopeSelector from '../../../../account/Media/MediaScopeSelector';
import MediaSearchBar from '../../../../account/Media/MediaSearchBar';
import MediaItemPreview from '../../../../common/Media/mediaItemPreview';
import { PageBuilderSettingsSection, PageBuilderSettingsTable } from '../PageBuilderSettingsTable';
import VyTooltip from '../../../../common/VyTooltip';
import { MdFileDownload, MdFileDownloadOff } from 'react-icons/md';

interface MediaSettingsTableInterface {
  component: MediaComponentInterface;
  onChange: (c: MediaComponentInterface) => void;
}

const MediaSettingsTable = ({ component, onChange }: MediaSettingsTableInterface) => {
  const event = useSelector((state) => state.common.event);
  const organization = useSelector((state) => state.admin.event?.organization || state.common.event?.organization);

  const [media, setMedia] = useState<MediaInterface[]>([]);
  const [scope, setScope] = useState<'current' | 'all'>('current');
  const [componentMediaIds, setComponentMediaIds] = useState<string[]>(component.media?.map((m) => m.mediaObject?._id) || []);
  const [isLoading, setIsLoading] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    setComponentMediaIds(component.media?.map((m) => m.mediaObject._id) || []);
  }, [component]);

  async function fetch(query?: string) {
    if (!event) return;
    setIsLoading(true);
    try {
      const _media = scope === 'current' ? await getAllMedia(event._id, query) : await getOrgMedia(organization?._id!, query);
      setMedia(_media);
    } catch (e) {
      console.error(e);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetch();
  }, [event, scope]);

  async function reorder(startIndex: number, endIndex: number) {
    let result = Array.from(component.media || []);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    result = result.map((m, index) => {
      return { ...m, sortIndex: index };
    });
    result.sort((a, b) => a.sortIndex - b.sortIndex);
    onChange({ ...component, media: result });
  }

  async function deleteMedia(m: MediaInterface) {
    const _component = { ...component };

    const _media = _component.media ? [..._component.media] : [];
    const mediaToRemove = _media[componentMediaIds.indexOf(m._id)];
    _media.splice(componentMediaIds.indexOf(m._id), 1);
    _media
      .filter((m) => m.sortIndex > mediaToRemove.sortIndex)
      .forEach((m, index: number) => {
        _media[index + mediaToRemove.sortIndex] = { ...m, sortIndex: m.sortIndex - 1 };
      });
    _component.media = _media;
    onChange(_component);
  }

  function handleMediaClick(media: media) {
    let _component: any = { ...component };

    _component = {
      ...component,
      media: [...(component.media ? component.media : []), { mediaObject: media, sortIndex: component.media?.length }],
    };

    onChange(_component);
  }

  const settingSections: PageBuilderSettingsSection[] = [
    {
      name: t('pagebuilder:component-settings'),
      settings: [
        {
          label: t('pagebuilder:show-titles'),
          type: 'boolean',
          path: 'showTitles',
          tooltip: t('pagebuilder:show-titles-description'),
        },
      ],
    },
    {
      name: t('pagebuilder:available-media'),
      settings: [
        {
          content: (
            <>
              <MediaScopeSelector scope={scope} setScope={setScope} isMediaPage={false} />
              <MediaSearchBar onSearch={fetch} />
              {isLoading ? (
                <CircularProgress />
              ) : media && media.length !== 0 ? (
                <TableContainer component={Paper} className="max-h-96">
                  <Table>
                    <TableBody>
                      {media
                        .filter((media) => !component.media?.map((el) => el.mediaObject._id).includes(media._id))
                        .map((media) => (
                          <TableRow onClick={() => handleMediaClick(media)} key={media._id} className="cursor-pointer hover:bg-blue-200">
                            <TableCell component="th" scope="row">
                              <span className="break-all"> {media.name}</span>
                            </TableCell>
                            <TableCell align="right">
                              <MediaItemPreview media={media} />
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">{t('pagebuilder:no-media-available')}</Alert>
              )}
            </>
          ),
        },
      ],
    },
    {
      name: t('pagebuilder:selected-media'),
      settings: [
        {
          content: (
            <>
              {component.media && component.media.length > 0 ? (
                <TableContainer component={Paper} className="mt-4">
                  <Table>
                    <TableBody>
                      {component.media
                        .slice()
                        .sort((a, b) => a.sortIndex - b.sortIndex)
                        .map((media) => media.mediaObject)
                        .map((media, index: number) => {
                          const isDownloadable = component.media?.find((m) => m.mediaObject._id === media._id)?.isDownloadable;

                          return (
                            <TableRow key={media._id}>
                              <TableCell component="th" scope="row">
                                <div className="break-all"> {media.name}</div>
                              </TableCell>
                              <TableCell align="right">
                                <MediaItemPreview media={media} />
                              </TableCell>
                              <TableCell align="right">
                                <div className="flex space-x-0">
                                  <VyTooltip title={t(`pagebuilder:downloadable-media-description-${isDownloadable ? 'on' : 'off'}`)}>
                                    <IconButton
                                      onClick={() =>
                                        onChange({
                                          ...component,
                                          media: component?.media?.map((m) =>
                                            m.mediaObject._id === media._id ? { ...m, isDownloadable: !isDownloadable } : m
                                          ),
                                        })
                                      }>
                                      {isDownloadable ? <MdFileDownload /> : <MdFileDownloadOff />}
                                    </IconButton>
                                  </VyTooltip>
                                  <IconButton
                                    disabled={index + 1 === component.media?.length}
                                    style={{ outline: 'none' }}
                                    onClick={() => reorder(index, index + 1)}>
                                    <ArrowDownward color={index + 1 === component.media?.length ? 'action' : 'primary'} />
                                  </IconButton>
                                  <IconButton disabled={index === 0} style={{ outline: 'none' }} onClick={() => reorder(index, index - 1)}>
                                    <ArrowUpward color={index === 0 ? 'action' : 'primary'} />
                                  </IconButton>
                                  <IconButton style={{ outline: 'none' }} onClick={() => deleteMedia(media)}>
                                    <CloseRounded color="error" />
                                  </IconButton>
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info" className="mt-2">
                  {t('pagebuilder:no-media-selected')}
                </Alert>
              )}
            </>
          ),
        },
      ],
    },
  ];

  return <PageBuilderSettingsTable sections={settingSections} onChange={onChange} component={component} />;
};
export default MediaSettingsTable;
